import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";

const EMPTY = { name: "", company: "", email: "", phone: "", status: "active", notes: "" };

export default function OwnerClientsSection({ clients, onRefresh }) {
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const setField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await ownerApi.createClient(form);
      setForm(EMPTY);
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo guardar el cliente"));
    } finally {
      setSaving(false);
    }
  }

  async function changeStatus(client, status) {
    try {
      await ownerApi.updateClient(client.id, { status });
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo actualizar el cliente"));
    }
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent component="form" onSubmit={submit}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <PersonAddAltRoundedIcon />
            <Typography variant="h6" sx={{ fontWeight: 950 }}>Nuevo cliente</Typography>
          </Stack>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1.4 }}>
            <TextField required label="Nombre" value={form.name} onChange={setField("name")} />
            <TextField label="Empresa / marca" value={form.company} onChange={setField("company")} />
            <TextField label="Email" type="email" value={form.email} onChange={setField("email")} />
            <TextField label="Teléfono" value={form.phone} onChange={setField("phone")} />
            <TextField select label="Estado" value={form.status} onChange={setField("status")}>
              <MenuItem value="lead">Potencial</MenuItem>
              <MenuItem value="active">Activo</MenuItem>
            </TextField>
            <TextField label="Notas" value={form.notes} onChange={setField("notes")} />
          </Box>
          <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 2, textTransform: "none", fontWeight: 900 }}>
            {saving ? "Guardando..." : "Guardar cliente"}
          </Button>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 950 }}>Mis clientes</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" }, gap: 1.3 }}>
        {clients.map((client) => (
          <Card key={client.id} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>{client.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[client.company, client.email, client.phone].filter(Boolean).join(" · ") || "Sin datos de contacto todavía"}
                  </Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  value={client.status}
                  onChange={(event) => changeStatus(client, event.target.value)}
                  sx={{ minWidth: 125 }}
                >
                  <MenuItem value="lead">Potencial</MenuItem>
                  <MenuItem value="active">Activo</MenuItem>
                  <MenuItem value="inactive">Inactivo</MenuItem>
                </TextField>
              </Stack>
            </CardContent>
          </Card>
        ))}
        {!clients.length ? <Typography color="text.secondary">Todavía no cargaste clientes.</Typography> : null}
      </Box>
    </Stack>
  );
}
